/**
 * Product analytics (PostHog) for frontend events, funnels, and cohorts.
 *
 * PRIVACY BEST PRACTICES:
 * - Database user IDs are never sent to PostHog. Identified analytics, when
 *   needed, uses only a server-issued opaque analytics ID.
 * - Never send PII (email, name, phone) to analytics
 * - Only send anonymized user ID + non-PII traits (role, etc.)
 * - Backend uses unhashed ID in App Insights (internal only)
 * - Consent: set analytics_consent to 'false' in localStorage to opt out (no init, no events)
 *
 * RACE CONDITION HANDLING:
 * - Events are queued if PostHog SDK hasn't loaded yet
 * - Queue has size limits to prevent memory leaks
 * - Stale events (>5 min old) are discarded
 * - All operations are fail-safe (never break the app)
 *
 * CORRELATION:
 * - Session ID is included in all events for tracing frontend → backend
 * - Session ID is also sent in API request headers (X-Session-ID)
 * - Backend attaches session ID to OpenTelemetry spans
 */

import { validateNoPII } from "../lib/analyticsUtils";
import { getSessionId } from "../lib/sessionGet";

/** localStorage key for analytics consent. Opt-in: only 'true' = allow; missing or 'false' = no tracking. */
export const ANALYTICS_CONSENT_KEY = "analytics_consent";

/**
 * Check whether analytics is allowed (opt-in: user has explicitly accepted).
 * Safe for SSR / missing localStorage. Returns false until user opts in.
 */
function hasAnalyticsConsent(): boolean {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    return window.localStorage.getItem(ANALYTICS_CONSENT_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * Get current consent state for UI (e.g. consent banner visibility).
 * Returns 'true' | 'false' | null (null = user has not made a choice yet).
 */
export function getAnalyticsConsent(): "true" | "false" | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const v = window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
    if (v === "true") return "true";
    if (v === "false") return "false";
    return null;
  } catch {
    return null;
  }
}

/**
 * Set analytics consent (for use by consent banner or settings).
 * Call with true to opt in (then call initProductAnalytics() to start tracking).
 * Call with false to opt out.
 */
export function setAnalyticsConsent(granted: boolean): void {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(
        ANALYTICS_CONSENT_KEY,
        granted ? "true" : "false"
      );
    }
    _cachedAnalyticsEnabled = null; // Invalidate so next identify/capture recomputes
  } catch {
    // Ignore (e.g. private browsing)
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PostHog = any;

interface PendingEvent {
  type: "identify" | "capture" | "reset";
  data: {
    analyticsId?: string;
    traits?: Record<string, string | number | boolean>;
    eventName?: string;
    properties?: Record<string, string | number | boolean>;
  };
  timestamp: number;
}

let posthog: PostHog | null = null;
let isInitialized = false;
const eventQueue: PendingEvent[] = [];
const MAX_QUEUE_SIZE = 100; // Prevent memory leaks
const EVENT_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

/** PostHog free tier: 1M events/month. We sample capture() and cap per-session to stay under. */
const MAX_CAPTURES_PER_SESSION = 500; // Per-session cap so one tab cannot blow the budget

let sessionCaptureCount = 0;

/**
 * Get env var. Uses process.env only so Jest can parse this file (no import.meta).
 * Vite replaces process.env with import.meta.env at build time (see vite.config.ts).
 */
function getEnv(key: string): string | undefined {
  if (
    typeof process !== "undefined" &&
    process.env &&
    typeof (process.env as Record<string, unknown>)[key] === "string"
  ) {
    return (process.env as Record<string, string>)[key];
  }
  return undefined;
}

/** PostHog API key (for components that need to check if analytics is configured). Jest-safe: uses process.env. */
export function getPostHogKey(): string | undefined {
  return getEnv("VITE_POSTHOG_KEY");
}

/** True when running in development (Vite MODE=development or DEV=true). */
function isDev(): boolean {
  return (
    getEnv("MODE") === "development" ||
    getEnv("DEV") === "true" ||
    (typeof process !== "undefined" &&
      (process.env as Record<string, unknown>)?.NODE_ENV !== "production")
  );
}

/**
 * Analytics logging: verbose logs only in DEV; errors always; one info line in production when ready.
 * Use logAnalytics('info', ...) for the single production "deployed correctly" signal.
 */
function logAnalytics(
  level: "log" | "warn" | "error" | "info",
  ...args: unknown[]
): void {
  if (level === "error") {
    console.error("[Analytics]", ...args);
    return;
  }
  if (level === "info") {
    console.info("[Analytics]", ...args);
    return;
  }
  if (isDev()) {
    if (level === "log") console.log("[Analytics]", ...args);
    else if (level === "warn") console.warn("[Analytics]", ...args);
  }
}

/** Cached result of "can we send analytics?" (key + window + consent). Cleared when consent changes. */
let _cachedAnalyticsEnabled: boolean | null = null;

/**
 * Whether analytics can run (key set, browser, user consented). Cached until setAnalyticsConsent() is called.
 */
function isAnalyticsEnabled(): boolean {
  if (_cachedAnalyticsEnabled !== null) return _cachedAnalyticsEnabled;
  _cachedAnalyticsEnabled = !!(
    getEnv("VITE_POSTHOG_KEY") &&
    typeof window !== "undefined" &&
    hasAnalyticsConsent()
  );
  return _cachedAnalyticsEnabled;
}

/**
 * Capture sample rate (0.0–1.0). Default 0.2 (20%) to stay under PostHog free tier (1M events/month).
 * Set VITE_POSTHOG_CAPTURE_SAMPLE_RATE=1.0 only if you monitor volume and stay under 1M.
 */
function getCaptureSampleRate(): number {
  const raw = getEnv("VITE_POSTHOG_CAPTURE_SAMPLE_RATE");
  if (raw == null || raw === "") return 0.2;
  const n = parseFloat(raw);
  if (Number.isNaN(n) || n < 0 || n > 1) return 0.2;
  return n;
}

/** Parsed allowlist: when set, only these event names are sent (e.g. "feature_used,user_signed_in"). */
let _cachedCaptureEventAllowlist: Set<string> | null = null;

/**
 * When VITE_POSTHOG_CAPTURE_EVENTS is set (comma-separated names), only those events are captured.
 * Use when nearing 1M events/month to restrict to high-value events without code change.
 * When unset, all events are allowed.
 */
function getCaptureEventAllowlist(): Set<string> | null {
  if (_cachedCaptureEventAllowlist !== null)
    return _cachedCaptureEventAllowlist;
  const raw = getEnv("VITE_POSTHOG_CAPTURE_EVENTS");
  if (raw == null || raw === "") {
    _cachedCaptureEventAllowlist = null;
    return null;
  }
  const names = raw
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  _cachedCaptureEventAllowlist = names.length > 0 ? new Set(names) : null;
  return _cachedCaptureEventAllowlist;
}

function isCaptureEventAllowed(eventName: string): boolean {
  const allowlist = getCaptureEventAllowlist();
  if (allowlist === null) return true;
  return allowlist.has(eventName.trim().toLowerCase());
}

/**
 * Process a single event (identify, capture, or reset).
 * Handles hashing, validation, and error isolation.
 */
async function processEvent(event: PendingEvent): Promise<void> {
  if (!posthog) return;

  try {
    // Discard stale events (older than 5 minutes)
    if (Date.now() - event.timestamp > EVENT_EXPIRY_MS) {
      logAnalytics(
        "warn",
        `Discarding stale event (${event.type}), age: ${Math.round(
          (Date.now() - event.timestamp) / 1000
        )}s`
      );
      return;
    }

    switch (event.type) {
      case "identify": {
        if (!event.data.analyticsId) return;

        // Validate no PII in traits
        if (event.data.traits) {
          validateNoPII(event.data.traits);
        }

        posthog.identify(event.data.analyticsId, event.data.traits);
        break;
      }

      case "capture": {
        if (!event.data.eventName) return;
        // Validate no PII in properties
        if (event.data.properties) {
          validateNoPII(event.data.properties);
        }
        // Add session ID to all events for correlation
        const properties = {
          ...event.data.properties,
          session_id: getSessionId(),
        };
        posthog.capture(event.data.eventName, properties);
        break;
      }

      case "reset": {
        posthog.reset();
        break;
      }
    }
  } catch (error) {
    logAnalytics("error", `Event processing failed (${event.type}):`, error);
    // Don't throw - analytics should never break the app
  }
}

/**
 * Process all queued events after PostHog loads.
 * Events are processed in order, stale events are discarded.
 */
async function processEventQueue(): Promise<void> {
  if (!posthog || eventQueue.length === 0) return;

  logAnalytics("log", `Processing ${eventQueue.length} queued event(s)`);

  // Process events in order
  while (eventQueue.length > 0) {
    const event = eventQueue.shift();
    if (event) {
      await processEvent(event);
    }
  }
}

/**
 * Initialize PostHog when VITE_POSTHOG_KEY is set. Safe to call multiple times.
 * Call early in app lifecycle (e.g. main.tsx).
 * If events were queued before the SDK loaded, they are processed once the SDK is ready.
 */
export function initProductAnalytics(): void {
  const key = getEnv("VITE_POSTHOG_KEY");
  if (!key || typeof window === "undefined") return;
  if (!hasAnalyticsConsent()) return;

  if (posthog || isInitialized) return;

  try {
    // Dynamic import so tests and builds without the key don't need the lib
    import("posthog-js")
      .then(({ default: posthogJs }) => {
        const devMode =
          getEnv("MODE") === "development" || getEnv("DEV") === "true";
        (posthogJs as PostHog).init(key, {
          api_host: getEnv("VITE_POSTHOG_HOST") || "https://us.i.posthog.com",
          capture_pageview: true,
          person_profiles: "identified_only",
          ...(devMode ? { debug: true } : {}),
          loaded: (posthogInstance: PostHog) => {
            if (isDev()) {
              logAnalytics("log", "PostHog loaded successfully");
            } else {
              logAnalytics("info", "PostHog initialized (production).");
            }
            posthog = posthogInstance;
            isInitialized = true;
            // Process any queued events
            processEventQueue().catch((error) => {
              logAnalytics("error", "Failed to process event queue:", error);
            });
          },
        });
      })
      .catch((error) => {
        logAnalytics("error", "Failed to load PostHog SDK:", error);
        isInitialized = true; // Mark as initialized to stop queuing
      });
  } catch (error) {
    logAnalytics("error", "Failed to initialize PostHog:", error);
    isInitialized = true; // Mark as initialized to stop queuing
  }
}

/**
 * Identify the current user with a server-issued opaque analytics ID.
 *
 * PRIVACY: Do not pass a database ID, email, or a browser-derived hash here.
 * The value must be a high-entropy opaque ID minted by the backend specifically
 * for analytics. Browser-side hashing cannot safely anonymize low-entropy IDs.
 *
 * Call on login only when the authenticated-user response includes that opaque ID.
 * If PostHog is not loaded yet, the event is queued and processed when ready.
 *
 * @param analyticsId - Server-issued opaque analytics ID
 * @param traits - Non-PII traits only (e.g., role) - validated for PII
 */
export async function identify(
  analyticsId: string,
  traits?: Record<string, string | number | boolean>
): Promise<void> {
  try {
    if (!isAnalyticsEnabled()) return;

    const event: PendingEvent = {
      type: "identify",
      data: { analyticsId, traits },
      timestamp: Date.now(),
    };

    if (isInitialized && posthog) {
      await processEvent(event);
    } else {
      if (eventQueue.length < MAX_QUEUE_SIZE) {
        eventQueue.push(event);
      } else {
        logAnalytics("warn", "Event queue full, discarding oldest event");
        eventQueue.shift();
        eventQueue.push(event);
      }
    }
  } catch (error) {
    logAnalytics("error", "Identify failed:", error);
    // Never throw - analytics must not break the app
  }
}

/**
 * Capture a product analytics event. Use stable event names and non-PII properties.
 *
 * Properties are validated to ensure no PII is accidentally sent.
 * If PostHog is not loaded yet, the event is queued and processed when ready.
 *
 * @param eventName - Event identifier (e.g., 'feature_used', 'search_performed')
 * @param properties - Event properties (validated for PII)
 */
export function capture(
  eventName: string,
  properties?: Record<string, string | number | boolean>
): void {
  try {
    if (!isAnalyticsEnabled()) return;
    // Optional: when nearing 1M/month, set VITE_POSTHOG_CAPTURE_EVENTS to high-value names only (e.g. feature_used,login)
    if (!isCaptureEventAllowed(eventName)) return;

    // Free-tier safeguard: per-session cap so one session cannot exceed budget (1M/month = ~33k/day ≈ 500/session safe)
    if (sessionCaptureCount >= MAX_CAPTURES_PER_SESSION) return;
    // Free-tier safeguard: sample capture events to stay under 1M events/month (configurable)
    if (Math.random() >= getCaptureSampleRate()) return;
    sessionCaptureCount += 1;

    const event: PendingEvent = {
      type: "capture",
      data: { eventName, properties },
      timestamp: Date.now(),
    };

    if (isInitialized && posthog) {
      processEvent(event).catch((error) => {
        logAnalytics("error", `Failed to capture event "${eventName}":`, error);
      });
    } else {
      if (eventQueue.length < MAX_QUEUE_SIZE) {
        eventQueue.push(event);
      } else {
        logAnalytics("warn", "Event queue full, discarding oldest event");
        eventQueue.shift();
        eventQueue.push(event);
      }
    }
  } catch (error) {
    logAnalytics("error", "Capture failed:", error);
    // Never throw - analytics must not break the app
  }
}

/**
 * Clear the identified user and reset state. Call on logout.
 */
export function reset(): void {
  try {
    eventQueue.length = 0;

    const event: PendingEvent = {
      type: "reset",
      data: {},
      timestamp: Date.now(),
    };

    if (isInitialized && posthog) {
      processEvent(event).catch((error) => {
        logAnalytics("error", "Failed to reset:", error);
      });
    }
  } catch (error) {
    logAnalytics("error", "Reset failed:", error);
    // Never throw - analytics must not break the app
  }
}

export const productAnalytics = {
  init: initProductAnalytics,
  identify,
  capture,
  reset,
  setAnalyticsConsent,
};
